import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { FolderTree, LifeBuoy, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type RowAction,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { HelpArticle } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStore } from '@/shared/store/store-context';

import { DeleteHelpArticleDialog } from '../components/delete-help-article-dialog';
import { useHelpArticleColumns } from '../components/help-article-columns';
import { helpArticleFilters } from '../components/help-article-filters';
import { useHelpArticles } from '../hooks/use-help-articles';

const ROUTE_ID = '/_authed/_active/s/$storeId/help/' as const;
const ROUTE_PATH = '/s/$storeId/help/' as const;

function NewArticleButton({ storeId }: { storeId: string }) {
  const { t } = useTranslation('helpCenter');
  return (
    <Can permission="help.write">
      <Button asChild>
        <Link to="/s/$storeId/help/new" params={{ storeId }}>
          <Plus aria-hidden />
          {t('articles.new')}
        </Link>
      </Button>
    </Can>
  );
}

function HelpArticles() {
  const { t } = useTranslation('helpCenter');
  const store = useStore();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can('help.write');
  const [deleting, setDeleting] = useState<HelpArticle | null>(null);

  const filters = useMemo(
    () => helpArticleFilters({ t, queryClient, storeId: store.id }),
    [t, queryClient, store.id],
  );
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useHelpArticles(store.id, list.params);

  const open = useCallback(
    (row: HelpArticle) =>
      void navigate({
        to: '/s/$storeId/help/$articleId',
        params: { storeId: store.id, articleId: row.id },
      }),
    [navigate, store.id],
  );

  const rowActions = useMemo<Array<RowAction<HelpArticle>>>(
    () => [
      { id: 'open', label: t('actions.open'), icon: <Pencil aria-hidden />, onAction: open },
      {
        id: 'delete',
        label: t('actions.delete'),
        icon: <Trash2 aria-hidden />,
        variant: 'destructive',
        separatorBefore: true,
        hidden: () => !canWrite,
        onAction: (row) => setDeleting(row),
      },
    ],
    [canWrite, open, t],
  );

  const columns = useHelpArticleColumns(store.id);
  const rows = query.data?.items ?? [];
  const table = useDataTable({
    mode: 'server',
    data: rows,
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'help-articles',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && rows.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader
        title={t('articles.title')}
        description={t('articles.description', { store: store.name })}
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/s/$storeId/help/categories" params={{ storeId: store.id }}>
                <FolderTree aria-hidden />
                {t('articles.manageCategories')}
              </Link>
            </Button>
            <NewArticleButton storeId={store.id} />
          </>
        }
      />
      {isEmptyList ? (
        <EmptyState
          icon={LifeBuoy}
          title={t('articles.empty.title')}
          description={t('articles.empty.description')}
          action={<NewArticleButton storeId={store.id} />}
        />
      ) : (
        <DataTable
          table={table}
          search={{ placeholder: t('articles.search'), label: t('articles.search') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          rowActions={rowActions}
          onRowClick={open}
          getRowLabel={(row) => row.title}
          resourceName={{
            singular: t('articles.resource.one'),
            plural: t('articles.resource.other'),
          }}
          emptyState={{ title: t('articles.noMatches') }}
        />
      )}
      <DeleteHelpArticleDialog
        storeId={store.id}
        row={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function HelpArticlesPage() {
  return (
    <FeatureErrorBoundary name="helpCenter.articles">
      <HelpArticles />
    </FeatureErrorBoundary>
  );
}
